import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Loader2, FileText, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Modal reusable para previsualizar documentos (PDF/imágenes).
 * Dos modos:
 *   A) Documento único:
 *      <DocumentPreviewModal open url title filename />
 *   B) Lista con navegación:
 *      <DocumentPreviewModal open items={[{url,title,filename}, ...]} index={i}
 *                            onIndexChange={(i) => ...} />
 */
export default function DocumentPreviewModal({
  open, onClose,
  url, title, filename,
  items, index, onIndexChange,
}) {
  const navigable = Array.isArray(items) && items.length > 0 && typeof index === "number";
  const current = navigable ? items[Math.max(0, Math.min(index, items.length - 1))] : { url, title, filename };

  const [blobUrl, setBlobUrl] = useState(null);
  const [mime, setMime] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !current?.url) { setBlobUrl(null); setError(null); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    api.get(current.url, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        setMime(r.data.type || r.headers?.["content-type"] || "application/pdf");
        setBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(r.data); });
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.detail || "No se pudo cargar el documento");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, current?.url]);

  // Clean up object URL when modal closes
  useEffect(() => {
    if (!open && blobUrl) { URL.revokeObjectURL(blobUrl); setBlobUrl(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open || !navigable) return;
    const handler = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, navigable, index, items]);

  const goPrev = () => { if (navigable && index > 0) onIndexChange(index - 1); };
  const goNext = () => { if (navigable && index < items.length - 1) onIndexChange(index + 1); };

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl; a.download = current.filename || "documento.pdf";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const isImage = mime.startsWith("image/");
  const canPrev = navigable && index > 0;
  const canNext = navigable && index < items.length - 1;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white p-0 max-w-5xl w-[92vw] h-[88vh] flex flex-col overflow-hidden pr-10">
        <DialogHeader className="px-5 py-3 border-b border-slate-100 flex-row items-center justify-between gap-3 space-y-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <FileText size={18} className="text-blue-600 shrink-0" />
            <DialogTitle className="text-base font-display truncate">{current.title || "Documento"}</DialogTitle>
            {navigable && <span className="text-xs text-slate-400 shrink-0">· {index + 1} / {items.length}</span>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {navigable && (
              <>
                <button data-testid="doc-preview-prev" onClick={goPrev} disabled={!canPrev}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed" title="Anterior (←)">
                  <ChevronLeft size={16} />
                </button>
                <button data-testid="doc-preview-next" onClick={goNext} disabled={!canNext}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed" title="Siguiente (→)">
                  <ChevronRight size={16} />
                </button>
              </>
            )}
            <button data-testid="doc-preview-download" onClick={download} disabled={!blobUrl}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50">
              <Download size={13} /> Descargar
            </button>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-slate-100 overflow-hidden">
          {loading && (
            <div className="h-full flex items-center justify-center text-slate-400">
              <Loader2 size={22} className="animate-spin" />
            </div>
          )}
          {error && !loading && (
            <div className="h-full flex items-center justify-center text-sm text-red-500">{error}</div>
          )}
          {!loading && !error && blobUrl && (
            isImage ? (
              <div className="h-full w-full overflow-auto flex items-center justify-center p-4">
                <img src={blobUrl} alt={current.title} className="max-h-full max-w-full object-contain" />
              </div>
            ) : (
              <iframe data-testid="doc-preview-iframe" title={current.title || "documento"} src={blobUrl}
                className="w-full h-full border-0 bg-white" />
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
